import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  IconButton,
  Container,
  CircularProgress,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  BusinessCenter,
  Delete,
  Add
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import FileUploadComponent from '../common/FileUploadComponent';

const EmploymentSection = ({
  employmentList = [],
  setEmploymentList,
  onFileUpload,
  onFileRemove,
  validationErrors = {},
  onClearError,
  isFresher = false,
  isSubmitting,
  rejectedDocs
}) => {
  const theme = useTheme();

  const getRejectionError = (idx) => {
    if (!rejectedDocs) return null;
    const doc = rejectedDocs.find(d => d.documentType === 'Experience Documents' && d.index === idx);
    return doc ? `Rejected: ${doc.rejectionReason}` : null;
  };

  // Maps field name to the error key prefix used in validationErrors
  const fieldToErrorKey = {
    companyName: 'company',
    role: 'role',
    ctc: 'ctc',
    fromDate: 'fromDate',
    toDate: 'toDate',
  };

  const handleEmploymentChange = useCallback((index, field, value) => {
    setEmploymentList(prev =>
      (prev || []).map((item, i) => i === index ? { ...(item || {}), [field]: value } : item)
    );
    if (onClearError) {
      const errorPrefix = fieldToErrorKey[field] || field;
      onClearError(`${errorPrefix}_${index}`);
      // Also clear dateRange error when either date changes
      if (field === 'fromDate' || field === 'toDate') {
        onClearError(`dateRange_${index}`);
      }
    }
  }, [setEmploymentList, onClearError]);

  const addEmployment = useCallback(() => {
    setEmploymentList(prev => [
      ...(prev || []),
      {
        companyName: "",
        department: "",
        role: "",
        ctc: "",
        fromDate: "",
        toDate: "",
        address: "",
        isLastEmployment: false,
        offerLetter: [],
        relievingLetter: [],
        experienceLetter: [],
        payslips: [],
        incrementLetter: []
      }
    ]);
  }, [setEmploymentList]);

  const removeEmployment = useCallback((index) => {
    setEmploymentList(prev => (prev || []).filter((_, i) => i !== index));
  }, [setEmploymentList]);

  const getDocumentTypes = (idx) => [
    { label: 'Relieving Letter', type: 'relievingLetter', required: false },
    { label: 'Experience Letter', type: 'experienceLetter', required: false },
    { label: 'Last 3 Months Salary Slips', type: 'payslips', required: !isFresher && employmentList[idx]?.isLastEmployment }
  ];

  // Premium Custom Card Styling
  const customCardStyle = {
    borderRadius: '16px',
    border: '1px solid',
    borderColor: alpha(theme.palette.primary.main, 0.08),
    boxShadow: '0 4px 20px -2px rgba(117, 34, 209, 0.02)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 30px -4px rgba(117, 34, 209, 0.08)',
      borderColor: alpha(theme.palette.primary.main, 0.2)
    },
    overflow: 'hidden'
  };

  const cardHeaderStyle = (title, icon, onDelete) => (
    <Box sx={{
      px: { xs: 3, md: 4 },
      py: 3,
      borderBottom: '1px solid',
      borderColor: alpha(theme.palette.primary.main, 0.06),
      bgcolor: alpha(theme.palette.primary.main, 0.02),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          color: 'primary.main'
        }}>
          {icon}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
          {title}
        </Typography>
      </Box>
      {onDelete && (
        <IconButton
          onClick={onDelete}
          sx={{
            color: 'error.main',
            bgcolor: alpha(theme.palette.error.main, 0.05),
            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.12) }
          }}
          size="small"
        >
          <Delete sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
  );

  const inputGlobalStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      bgcolor: '#F8FAFC',
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderColor: '#E2E8F0',
        transition: 'all 0.2s ease',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-disabled': {
        bgcolor: alpha(theme.palette.action.disabledBackground, 0.03),
      },
      '&.Mui-disabled fieldset': {
        borderColor: '#F1F5F9',
      },
      '&.Mui-error fieldset': {
        borderColor: theme.palette.error.main,
      },
      '&.Mui-error:hover fieldset': {
        borderColor: theme.palette.error.dark,
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiSelect-select': {
      borderRadius: '12px',
    }
  };

  return (
    <Container maxWidth="xl" sx={{ position: 'relative' }}>
      {isSubmitting && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1000,
            borderRadius: '16px',
            backdropFilter: 'blur(4px)',
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main' }} />
        </Box>
      )}
      <Box sx={inputGlobalStyles}>
        {/* Top Welcome Banner Card */}
        <Card elevation={0} sx={{
          mb: 4,
          borderRadius: '16px',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #4D1293 100%)`,
          color: '#FFFFFF',
          p: { xs: 3, md: 4 },
          position: 'relative',
          boxShadow: '0 10px 25px -5px rgba(117, 34, 209, 0.3)'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, letterSpacing: '-0.5px' }}>
                Previous Employment
              </Typography>
              <Typography variant="body1" sx={{ color: alpha('#FFFFFF', 0.8), fontWeight: 400 }}>
                Share your work experience with us.
              </Typography>
            </Box>
            <BusinessCenter sx={{ fontSize: 80, color: alpha('#FFFFFF', 0.15), display: { xs: 'none', md: 'block' } }} />
          </Box>
        </Card>

        {employmentList.map((emp, idx) => (
          <Card key={idx} elevation={0} sx={{ ...customCardStyle, mb: 3 }}>
            {cardHeaderStyle(`Employment #${idx + 1}`, <BusinessCenter />, employmentList.length > 1 ? () => removeEmployment(idx) : null)}
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={{ xs: 2, md: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Company Name"
                    variant="outlined"
                    value={emp.companyName || ''}
                    onChange={e => handleEmploymentChange(idx, 'companyName', e.target.value)}
                    fullWidth
                    required
                    error={!!validationErrors[`company_${idx}`]}
                    helperText={validationErrors[`company_${idx}`]}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Department"
                    variant="outlined"
                    value={emp.department || ''}
                    onChange={e => handleEmploymentChange(idx, 'department', e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Role/Position"
                    variant="outlined"
                    value={emp.role || ''}
                    onChange={e => handleEmploymentChange(idx, 'role', e.target.value)}
                    fullWidth
                    error={!!validationErrors[`role_${idx}`]}
                    helperText={validationErrors[`role_${idx}`]}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="CTC (per annum)"
                    variant="outlined"
                    value={emp.ctc || ''}
                    onChange={e => handleEmploymentChange(idx, 'ctc', e.target.value)}
                    fullWidth
                    required
                    error={!!validationErrors[`ctc_${idx}`]}
                    helperText={validationErrors[`ctc_${idx}`]}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="From Date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    value={emp.fromDate || ''}
                    onChange={e => handleEmploymentChange(idx, 'fromDate', e.target.value)}
                    fullWidth
                    inputProps={{
                      max: emp.toDate || new Date().toISOString().split('T')[0]
                    }}
                    error={!!validationErrors[`fromDate_${idx}`] || !!validationErrors[`dateRange_${idx}`]}
                    helperText={validationErrors[`fromDate_${idx}`] || validationErrors[`dateRange_${idx}`]}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="To Date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    value={emp.toDate || ''}
                    onChange={e => handleEmploymentChange(idx, 'toDate', e.target.value)}
                    fullWidth
                    inputProps={{
                      min: emp.fromDate || undefined,
                      max: new Date().toISOString().split('T')[0]
                    }}
                    error={!!validationErrors[`toDate_${idx}`] || !!validationErrors[`dateRange_${idx}`]}
                    helperText={validationErrors[`toDate_${idx}`] || validationErrors[`dateRange_${idx}`]}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Office Address"
                    variant="outlined"
                    value={emp.address || ''}
                    onChange={e => handleEmploymentChange(idx, 'address', e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>
                {!isFresher && (
                  <Grid size={{ xs: 12 }}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={emp.isLastEmployment || false}
                            onChange={(e) => handleEmploymentChange(idx, 'isLastEmployment', e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Primary / Recent Employer
                          </Typography>
                        }
                      />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5 }}>
                        Enable this if this is your current or most recent job.
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Date Range Error Display */}
              {validationErrors[`dateRange_${idx}`] && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'error.main', borderRadius: '10px', color: '#FFFFFF' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                    ⚠️ {validationErrors[`dateRange_${idx}`]}
                  </Typography>
                </Box>
              )}

              {/* File Upload Section */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: theme.palette.text.secondary, fontWeight: 600 }}>
                  Attach Documents {!isFresher && <span style={{ color: 'red' }}>*</span>}
                </Typography>
                <Grid container spacing={3}>
                  {getDocumentTypes(idx).map((docType) => (
                    <Grid size={{ xs: 12, md: 4 }} key={docType.type}>
                      <Box sx={{
                        p: 2.5,
                        border: (!!validationErrors[`${docType.type}_${idx}`] || !!getRejectionError(idx)) 
                                ? `1px dashed ${theme.palette.error.main}` 
                                : `1px dashed ${theme.palette.divider}`,
                        borderRadius: 3,
                        bgcolor: '#F8FAFC',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.01)
                        }
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {docType.label} {docType.required && <span style={{ color: 'red' }}>*</span>}
                          </Typography>
                        </Box>
                        <FileUploadComponent
                          label="Upload"
                          files={emp[docType.type] || []}
                          onFileUpload={(files) => onFileUpload(files, 'employment', idx, docType.type)}
                          onFileRemove={(index) => onFileRemove('employment', idx, index, docType.type)}
                          multiple={true}
                          accept=".pdf,.jpg,.jpeg,.png"
                          buttonVariant="outlined"
                          showChips={true}
                          required={docType.required}
                          error={!!validationErrors[`${docType.type}_${idx}`] || !!getRejectionError(idx)}
                          helperText={getRejectionError(idx) || validationErrors[`${docType.type}_${idx}`]}
                        />
                        {(!emp[docType.type] || emp[docType.type].length === 0) && !validationErrors[`${docType.type}_${idx}`] && !getRejectionError(idx) && (
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>No file uploaded</Typography>
                        )}
                        {validationErrors[`${docType.type}_${idx}`] && !getRejectionError(idx) && (
                          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontWeight: 600 }}>
                            {validationErrors[`${docType.type}_${idx}`]}
                          </Typography>
                        )}
                        {getRejectionError(idx) && (
                          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontWeight: 600 }}>
                            {getRejectionError(idx)}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </CardContent>
          </Card>
        ))}

        {validationErrors['global_employment'] && (
          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'error.main', borderRadius: '10px', color: '#FFFFFF', boxShadow: '0 4px 12px rgba(211, 47, 47, 0.2)' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
              ⚠️ {validationErrors['global_employment']}
            </Typography>
          </Box>
        )}

        <Button
          variant="outlined"
          onClick={addEmployment}
          startIcon={<Add />}
          sx={{
            mt: 2,
            borderRadius: '12px',
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            fontWeight: 600,
            textTransform: 'none',
            px: 3,
            py: 1.25,
            borderWidth: '2px',
            '&:hover': {
              borderWidth: '2px',
              borderColor: theme.palette.primary.dark,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            },
            mx: { xs: 2, md: 0 }
          }}
        >
          Add Employment
        </Button>
      </Box>
    </Container>
  );
};

export default EmploymentSection;
